import { create } from 'zustand';
import { getUserGymId } from '@/utils/gymAuth';
import * as settingsWrapper from '@/services/edgeFunctionWrappers/settingsManagementWrapper';
import { mergeAthleteThemeConfig, type AthleteThemeConfig } from '@/lib/athleteTheme';

export interface Settings {
  appName?: string;
  appearance: {
    primaryColor: string;
    theme: string;
    logoLightUrl?: string;
    logoDarkUrl?: string;
    brandName?: string;
    athleteTheme?: AthleteThemeConfig;
  };
  roles: {
    defaultRole: string;
    permissions: {
      trainerCanEditClasses: boolean;
      userCanViewAllClients: boolean;
      adminOnlyPayments: boolean;
    };
  };
  language: {
    defaultLanguage: string;
    currency: "TND" | "EUR" | "SAR";
  };
  schedule: {
    slotDuration: number;
    startTime: string;
    endTime: string;
    bookingAdvanceDays: number;
    defaultBypassCooldown: boolean;
    hideAvailableSlotsForClients: boolean;
    /**
     * Jours de la semaine ouverts aux réservations et à l'affichage du planning.
     * 0 = Dimanche, 1 = Lundi, ..., 6 = Samedi.
     * Défaut: [1,2,3,4,5,6] (Lun -> Sam, dimanche fermé) pour préserver le comportement existant.
     */
    openDaysOfWeek: number[];
  };
}

/** Forme de la valeur `app_config` stockée en base (sous-ensemble de Settings). */
type StoredSettings = Partial<Settings> & Record<string, unknown>;

interface SettingsStore {
  settings: Settings;
  isLoading: boolean;
  error: string | null;
  /**
   * Salle pour laquelle `settings` reflète réellement la base (`null` tant
   * qu'aucune lecture n'a abouti). Sert de garde-fou à l'enregistrement.
   */
  loadedGymId: string | null;
  /** Une ligne `app_config` existe-t-elle côté serveur pour `loadedGymId` ? */
  remoteSettingsExist: boolean;
  fetchSettings: () => Promise<void>;
  updateSettings: (settings: Partial<Settings>) => Promise<void>;
}

const initialSettings: Settings = {
  appName: null,
  appearance: {
    primaryColor: "#F59E0B",
    theme: "light",
    logoLightUrl: "",
    logoDarkUrl: "",
    brandName: "",
  },
  roles: {
    defaultRole: "user",
    permissions: {
      trainerCanEditClasses: true,
      userCanViewAllClients: false,
      adminOnlyPayments: true,
    },
  },
  language: {
    defaultLanguage: "fr",
    currency: "TND",
  },
  schedule: {
    slotDuration: 45,
    startTime: "08:00",
    endTime: "21:00",
    bookingAdvanceDays: 30,
    defaultBypassCooldown: false,
    hideAvailableSlotsForClients: false,
    openDaysOfWeek: [1, 2, 3, 4, 5, 6],
  },
};

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  settings: initialSettings,
  isLoading: false,
  error: null,
  loadedGymId: null,
  remoteSettingsExist: false,
  
  fetchSettings: async () => {
    set({ isLoading: true, error: null });
    try {
      console.log('📥 Fetching settings via edge function...');
      
      const gymId = getUserGymId();
      
      if (!gymId) {
        console.log('⚠️ No gym selected, skipping settings fetch');
        set({
          settings: initialSettings,
          isLoading: false,
          loadedGymId: null,
          remoteSettingsExist: false,
        });
        return;
      }
      
      // Fetch settings and app_name in parallel via edge function
      const [settingsData, appName] = await Promise.all([
        settingsWrapper.getSettings(gymId, 'app_config'),
        settingsWrapper.getAppName(gymId)
      ]);
      
      if (settingsData?.value) {
        console.log('✅ Settings loaded successfully for gym:', settingsData.gym_id);
        
        const dbSettings = settingsData.value as Record<string, any>;
        
        const mergedSettings: Settings = {
          appName: appName,
          appearance: {
            ...initialSettings.appearance,
            ...(dbSettings.appearance || {}),
            athleteTheme: dbSettings.appearance?.athleteTheme,
          },
          roles: {
            ...initialSettings.roles,
            ...(dbSettings.roles || {}),
            permissions: {
              ...initialSettings.roles.permissions,
              ...(dbSettings.roles?.permissions || {}),
            },
          },
          language: {
            ...initialSettings.language,
            ...(dbSettings.language || {}),
          },
          schedule: {
            ...initialSettings.schedule,
            ...(dbSettings.schedule || {}),
          },
        };
        
        set({
          settings: mergedSettings,
          isLoading: false,
          loadedGymId: gymId,
          remoteSettingsExist: true,
        });
      } else {
        console.log('⚠️ No settings found, using defaults');
        set({
          settings: { ...initialSettings, appName },
          isLoading: false,
          loadedGymId: gymId,
          remoteSettingsExist: false,
        });
      }
    } catch (error) {
      console.error('💥 Failed to fetch settings:', error);
      // On ne marque PAS la salle comme chargée : `updateSettings` refusera
      // d'écrire tant qu'on n'a pas une base de fusion fiable.
      set({
        error: "Failed to fetch settings",
        isLoading: false,
        loadedGymId: null,
        remoteSettingsExist: false,
      });
    }
  },
  
  updateSettings: async (newSettings) => {
    set({ isLoading: true, error: null });
    try {
      const gymId = getUserGymId();
      
      if (!gymId) {
        throw new Error('No gym selected. Please select a gym first.');
      }

      // Relecture avant fusion. `getSettings` renvoie `null` aussi bien quand
      // la lecture échoue (session expirée) que quand aucune ligne n'existe :
      // retomber sur `initialSettings` revenait à ÉCRIRE les valeurs d'usine
      // par-dessus la configuration réelle de la salle (audit lot 7).
      const currentData = await settingsWrapper.getSettings(gymId, 'app_config');
      const { loadedGymId, remoteSettingsExist, settings: loadedSettings } = get();

      let currentSettings: StoredSettings;
      if (currentData?.value) {
        currentSettings = currentData.value as StoredSettings;
      } else if (loadedGymId === gymId && remoteSettingsExist) {
        // Une configuration existe pour cette salle mais on n'a pas pu la
        // relire : on refuse d'écrire plutôt que de l'écraser.
        throw new Error(
          "Impossible de relire la configuration de la salle : enregistrement annulé pour ne pas écraser les réglages existants."
        );
      } else if (loadedGymId === gymId) {
        // Aucune ligne côté serveur : la base de fusion est l'état chargé.
        currentSettings = loadedSettings as StoredSettings;
      } else {
        throw new Error(
          "Les réglages de cette salle n'ont pas été chargés : enregistrement annulé."
        );
      }
      
      // Merge the new settings with current settings
      const updatedSettings = {
        ...currentSettings,
        appearance: {
          ...currentSettings.appearance,
          ...(newSettings.appearance || {}),
          athleteTheme: newSettings.appearance?.athleteTheme
            ? mergeAthleteThemeConfig(
                (currentSettings.appearance as Settings['appearance'] | undefined)?.athleteTheme,
                newSettings.appearance.athleteTheme,
              )
            : (currentSettings.appearance as Settings['appearance'] | undefined)?.athleteTheme,
        },
        roles: {
          ...currentSettings.roles,
          ...(newSettings.roles || {}),
          permissions: {
            ...currentSettings.roles?.permissions,
            ...(newSettings.roles?.permissions || {}),
          },
        },
        language: {
          ...currentSettings.language,
          ...(newSettings.language || {}),
        },
        schedule: {
          ...currentSettings.schedule,
          ...(newSettings.schedule || {}),
        },
      };
      
      // Update via edge function
      await settingsWrapper.updateSettings(gymId, 'app_config', updatedSettings);
      
      // Update local state (keep appName from current state)
      set((state) => ({ 
        settings: { ...updatedSettings, appName: state.settings.appName } as Settings,
        isLoading: false,
        loadedGymId: gymId,
        remoteSettingsExist: true,
      }));
      
      return Promise.resolve();
    } catch (error) {
      console.error('Failed to update settings:', error);
      set({ error: "Failed to update settings", isLoading: false });
      return Promise.reject(error);
    }
  }
}));
